import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useClassrooms } from '../hooks/useClassrooms';
import { CalendarIcon, ClockIcon, PlayIcon, VideoCameraIcon, BookOpenIcon, SparklesIcon, RocketLaunchIcon, TrophyIcon } from '@heroicons/react/24/outline';
import { collection, onSnapshot, orderBy, query, where, limit, Timestamp, getDocs } from 'firebase/firestore';
import { db, functions } from '../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { ScheduledMeeting, Classroom } from '../types';

import MeetingDetailsTabs from '../components/MeetingDetailsTabs';
import AIChatButton from '../components/AIChatButton';

interface AggregatedMeeting extends ScheduledMeeting {
  classroomId: string;
  classroomName: string;
}

const LiveMeetings: React.FC = () => {
  const { currentUser } = useAuth();
  const { getAllUserClassrooms } = useClassrooms();
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [meetings, setMeetings] = useState<AggregatedMeeting[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'upcoming' | 'live' | 'previous'>('upcoming');

  const unsubRefs = useRef<Array<() => void>>([]);

  // Load classrooms the user belongs to
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!currentUser?.uid) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const all = await getAllUserClassrooms();
        // Trust getAllUserClassrooms to return only relevant classrooms; do not over-filter
        if (!cancelled) setClassrooms(all || []);
      } catch (e) {
        if (!cancelled) setError('Failed to load classrooms');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [currentUser?.uid]); // Remove getAllUserClassrooms from dependency array

  // Subscribe to meetings for each classroom (prefer one-time reads by default)
  useEffect(() => {
    // Cleanup previous subscriptions
    unsubRefs.current.forEach((fn) => fn && fn());
    unsubRefs.current = [];

    const byId = new Map<string, AggregatedMeeting>();
    // While attaching new listeners, indicate loading until at least one snapshot returns
    setLoading(true);

    const attach = (classroom: Classroom) => {
      const addMeetings = (loaded: ScheduledMeeting[]) => {
        loaded.forEach((m) => {
          const key = `${classroom.id}_${m.id}`;
          byId.set(key, {
            ...m,
            classroomId: classroom.id as string,
            classroomName: classroom.name,
          } as AggregatedMeeting);
        });
        // Emit sorted by start time
        const list = Array.from(byId.values()).sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
        setMeetings(list);
        // As soon as we receive any snapshot, stop loading indicator
        setLoading(false);
      };

      const normalizeMeeting = (data: any, id: string): ScheduledMeeting => {
        const rawStart = data.startTime;
        const normalizedStart = rawStart?.toDate ? rawStart.toDate().toISOString() : (typeof rawStart === 'string' ? rawStart : new Date(rawStart).toISOString());
        return { ...data, id, startTime: normalizedStart } as ScheduledMeeting;
      };

      try {
        const refPrimary = collection(db, 'classrooms', classroom.id as string, 'meetings');
        const nowTs = Timestamp.fromDate(new Date(Date.now() - 24 * 60 * 60 * 1000));
        const qPrimary = query(
          refPrimary,
          where('startTime', '>=', nowTs),
          orderBy('startTime', 'asc'),
          limit(100)
        );
        // One-time read instead of real-time listener
        getDocs(qPrimary).then((snap) => {
          const loaded: ScheduledMeeting[] = [];
          snap.forEach((d) => loaded.push(normalizeMeeting(d.data(), d.id)));
          addMeetings(loaded);
        }).catch(() => {});
      } catch {}

      try {
        const refLegacy = collection(db, 'classrooms', classroom.id as string, 'meetingns');
        const nowTs = Timestamp.fromDate(new Date(Date.now() - 24 * 60 * 60 * 1000));
        const qLegacy = query(
          refLegacy,
          where('startTime', '>=', nowTs),
          orderBy('startTime', 'asc'),
          limit(100)
        );
        getDocs(qLegacy).then((snap) => {
          const loaded: ScheduledMeeting[] = [];
          snap.forEach((d) => loaded.push(normalizeMeeting(d.data(), d.id)));
          addMeetings(loaded);
        }).catch(() => {});
      } catch {}
    };

    (classrooms || []).forEach((c) => c?.id && attach(c));
    // If there are no classrooms, stop loading immediately
    if (!classrooms || classrooms.length === 0) {
      setLoading(false);
    }

    return () => {
      unsubRefs.current.forEach((fn) => fn && fn());
      unsubRefs.current = [];
    };
  }, [classrooms]);

  // Utilities
  const now = new Date();
  const ongoingMeetings = useMemo(() => {
    return meetings.filter((m) => {
      const start = new Date(m.startTime);
      const end = new Date(start.getTime() + (m.duration || 60) * 60000);
      if (m.status === 'cancelled' || m.status === 'finished') return false;
      return now >= start && now <= end;
    });
  }, [meetings, now]);

  const upcomingMeetings = useMemo(() => {
    return meetings.filter((m) => {
      const start = new Date(m.startTime);
      if (m.status === 'cancelled' || m.status === 'finished') return false;
      return start > now;
    });
  }, [meetings, now]);

  const pastMeetings = useMemo(() => {
    return meetings.filter((m) => {
      const start = new Date(m.startTime);
      const end = new Date(start.getTime() + (m.duration || 60) * 60000);
      return m.status === 'finished' || end < now;
    });
  }, [meetings, now]);

  const formatTime = (d: string) => new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Africa/Accra' });
  const formatDate = (d: string) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'Africa/Accra' });

  const joinMeeting = async (meeting: AggregatedMeeting) => {
    try {
      if (!currentUser?.uid) throw new Error('Not authenticated');
      const callable = httpsCallable(functions, 'joinZoomMeeting');
      const result = await callable({ meetingId: meeting.id, classroomId: meeting.classroomId, userId: currentUser.uid });
      const data = result.data as any;
      const url = data?.meeting?.joinUrl || meeting.joinUrl;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError('Failed to join meeting');
    }
  };

  const startMeeting = async (meeting: AggregatedMeeting) => {
    try {
      if (!currentUser?.uid) throw new Error('Not authenticated');
      const callable = httpsCallable(functions, 'startZoomMeeting');
      const result = await callable({ meetingId: meeting.id, classroomId: meeting.classroomId, userId: currentUser.uid });
      const data = result.data as any;
      const url = data?.meeting?.startUrl || meeting.startUrl;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError('Failed to start meeting');
    }
  };

  const watchRecording = async (meeting: AggregatedMeeting) => {
    try {
      setInfo(null);
      const existing = (meeting as any).recordingShareUrl;
      if (existing) {
        window.open(existing, '_blank', 'noopener,noreferrer');
        return;
      }
      if (!currentUser?.uid) throw new Error('Not authenticated');
      const callable = httpsCallable(functions, 'fetchZoomRecording');
      const result = await callable({ meetingId: meeting.id, classroomId: meeting.classroomId, userId: currentUser.uid });
      const data = result.data as any;
      const url = data?.shareUrl;
      if (data?.status === 'processing' && !url) {
        setInfo('The recording is being processed. You will be notified when it is ready. Please try again later.');
        return;
      }
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        setError('Recording not available yet.');
      }
    } catch (e) {
      setError('Failed to fetch recording');
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg border border-gray-200 text-center">
          <VideoCameraIcon className="w-10 h-10 text-gray-400 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-gray-900">Live Meetings</h2>
          <p className="text-sm text-gray-600 mt-1">Please sign in to view your live classroom meetings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 overflow-x-hidden">
      {/* Hero Section */}
      <section className="hero-section relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 overflow-hidden">
        {/* Main Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12" style={{ width: '100%', maxWidth: '100vw' }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8 items-center" style={{ width: '100%' }}>
            
            {/* Left Side - Text Content */}
            <div className="text-center lg:text-left space-y-3 sm:space-y-4" style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
              {/* Welcome Badge */}
              <div className="inline-flex items-center px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white text-xs font-medium">
                <VideoCameraIcon className="h-3 w-3 mr-1.5 text-blue-300" />
                Live Classroom Sessions
              </div>

              {/* Main Headline */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-white">
                <span className="block">Join Your</span>
                <span className="block bg-gradient-to-r from-blue-200 via-cyan-200 to-indigo-200 bg-clip-text text-transparent">
                  Live Meetings
                </span>
              </h1>

              {/* Subtitle */}
              <p className="text-base sm:text-lg text-blue-100 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                Connect with your classmates and instructors in real-time, participate in interactive sessions, and never miss an important classroom meeting.
              </p>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-2 py-3" style={{ width: '100%', maxWidth: '100%' }}>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                    {ongoingMeetings.length}
                  </div>
                  <div className="text-xs text-blue-200">Live Now</div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                    {upcomingMeetings.length}
                  </div>
                  <div className="text-xs text-blue-200">Upcoming</div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                    {pastMeetings.length}
                  </div>
                  <div className="text-xs text-blue-200">Recordings</div>
                </div>
              </div>

              {/* Call-to-Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center lg:justify-start" style={{ width: '100%', maxWidth: '100%' }}>
                <button
                  onClick={() => setActiveTab('live')}
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-lg text-sm"
                >
                  <PlayIcon className="h-4 w-4 mr-1.5" />
                  Join Live Session
                </button>
                <button
                  onClick={() => setActiveTab('upcoming')}
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold rounded-lg hover:bg-white/20 transition-all duration-200 text-sm"
                >
                  <CalendarIcon className="h-4 w-4 mr-1.5" />
                  View Schedule
                </button>
              </div>
            </div>

            {/* Right Side - Visual Elements - Hidden on mobile for stability */}
            <div className="relative hidden xl:block">
              {/* Meeting Status Cards */}
              <div className="space-y-4">
                {/* Live Meeting Card */}
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center">
                      <PlayIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Live Sessions</h3>
                      <p className="text-blue-200 text-sm">{ongoingMeetings.length} meetings happening now</p>
                    </div>
                  </div>
                </div>

                {/* Upcoming Meetings Card */}
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 ml-8">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center">
                      <CalendarIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Upcoming</h3>
                      <p className="text-blue-200 text-sm">{upcomingMeetings.length} meetings scheduled</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-8 sm:h-12 text-gray-50" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25" fill="currentColor"></path>
            <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5" fill="currentColor"></path>
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" fill="currentColor"></path>
          </svg>
        </div>
      </section>

      {/* Main Dashboard Content */}
      <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8 pt-0 sm:pt-6 relative z-20 space-y-4 sm:space-y-8">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>
        )}
        {info && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700">{info}</div>
        )}

        {/* Tab-based layout */}
        <div className="bg-white rounded-xl border border-gray-200">
          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200 overflow-x-auto">
            <button
              onClick={() => setActiveTab('upcoming')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors relative inline-flex items-center justify-center whitespace-nowrap flex-1 ${
                activeTab === 'upcoming'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <CalendarIcon className="w-4 h-4 mr-2" />
              Upcoming
              {upcomingMeetings.length > 0 && (
                <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
                  {upcomingMeetings.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('live')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors relative inline-flex items-center justify-center whitespace-nowrap flex-1 ${
                activeTab === 'live'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <PlayIcon className="w-4 h-4 mr-2" />
              Live Now
              {ongoingMeetings.length > 0 && (
                <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
                  {ongoingMeetings.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('previous')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors relative inline-flex items-center justify-center whitespace-nowrap flex-1 ${
                activeTab === 'previous'
                  ? 'border-gray-500 text-gray-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <PlayIcon className="w-4 h-4 mr-2" />
              Recordings
              {pastMeetings.length > 0 && (
                <span className="ml-2 inline-flex items-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
                  {pastMeetings.length}
                </span>
              )}
            </button>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'upcoming' && (
              <div className="space-y-4">
                {upcomingMeetings.length > 0 ? (
                  upcomingMeetings.map((m) => (
                    <div key={`up_${m.classroomId}_${m.id}`} className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="min-w-0 pr-4">
                          <div className="text-lg font-semibold text-gray-900 truncate">{m.topic || 'Untitled Meeting'}</div>
                          <div className="text-sm text-gray-600 truncate mt-1">Classroom: {m.classroomName}</div>
                          <div className="flex items-center text-sm text-gray-600 mt-2 space-x-4">
                            <span className="inline-flex items-center"><ClockIcon className="w-4 h-4 mr-1" /> {formatTime(m.startTime)} • {m.duration}m</span>
                            <span className="inline-flex items-center"><CalendarIcon className="w-4 h-4 mr-1" /> {formatDate(m.startTime)}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          {m.hostId === currentUser.uid && (
                            <button
                              onClick={() => startMeeting(m)}
                              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 inline-flex items-center"
                            >
                              <PlayIcon className="w-4 h-4 mr-2" /> Start
                            </button>
                          )}
                          <button
                            onClick={() => joinMeeting(m)}
                            className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 inline-flex items-center"
                          >
                            <PlayIcon className="w-4 h-4 mr-2" /> Join
                          </button>
                        </div>
                        <MeetingDetailsTabs meetingId={m.id} />
                      </div>
                    </div>
                  ))
                ) : loading ? (
                  <div className="text-center text-gray-500 py-8">Loading...</div>
                ) : (
                  <div className="text-center text-gray-500 py-8">No upcoming meetings.</div>
                )}
              </div>
            )}

            {activeTab === 'live' && (
              <div className="space-y-4">
                {ongoingMeetings.length > 0 ? (
                  ongoingMeetings.map((m) => (
                    <div key={`live_${m.classroomId}_${m.id}`} className="border border-green-200 bg-green-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="min-w-0 pr-4">
                          <div className="text-lg font-semibold text-gray-900 truncate">{m.topic || 'Untitled Meeting'}</div>
                          <div className="text-sm text-gray-600 truncate mt-1">Classroom: {m.classroomName}</div>
                          <div className="flex items-center text-sm text-gray-600 mt-2 space-x-4">
                            <span className="inline-flex items-center"><ClockIcon className="w-4 h-4 mr-1" /> {formatTime(m.startTime)} • {m.duration}m</span>
                            <span className="inline-flex items-center"><CalendarIcon className="w-4 h-4 mr-1" /> {formatDate(m.startTime)}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => m.hostId === currentUser?.uid ? startMeeting(m) : joinMeeting(m)}
                          className={`px-4 py-2 text-white rounded-md text-sm font-medium inline-flex items-center ${
                            m.hostId === currentUser?.uid 
                              ? 'bg-blue-600 hover:bg-blue-700' 
                              : 'bg-red-600 hover:bg-red-700'
                          }`}
                        >
                          <PlayIcon className="w-4 h-4 mr-2" />
                          {m.hostId === currentUser?.uid ? 'Start Live' : 'Join Live'}
                        </button>
                        <MeetingDetailsTabs meetingId={m.id} />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-gray-500 py-8">No live meetings at the moment.</div>
                )}
              </div>
            )}

            {activeTab === 'previous' && (
              <div className="space-y-4">
                {pastMeetings.length > 0 ? (
                  pastMeetings.map((m) => (
                    <div key={`prev_${m.classroomId}_${m.id}`} className="border border-gray-200 bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="min-w-0 pr-4">
                          <div className="text-lg font-semibold text-gray-900 truncate">{m.topic || 'Untitled Meeting'}</div>
                          <div className="text-sm text-gray-600 truncate mt-1">Classroom: {m.classroomName}</div>
                          <div className="flex items-center text-sm text-gray-600 mt-2 space-x-4">
                            <span className="inline-flex items-center"><ClockIcon className="w-4 h-4 mr-1" /> {formatTime(m.startTime)} • {m.duration}m</span>
                            <span className="inline-flex items-center"><CalendarIcon className="w-4 h-4 mr-1" /> {formatDate(m.startTime)}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => watchRecording(m)}
                          className="px-4 py-2 bg-gray-700 text-white rounded-md text-sm font-medium hover:bg-gray-800 inline-flex items-center"
                        >
                          <PlayIcon className="w-4 h-4 md:mr-2" />
                          <span className="hidden md:inline">Watch Recording</span>
                        </button>
                      </div>
                      
                      {/* Joined and Activity tabs below the card */}
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <MeetingDetailsTabs meetingId={m.id} />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-gray-500 py-8">No previous live meetings.</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* AI Chat Button */}
      <AIChatButton />
    </div>
  );
};

export default LiveMeetings;


